import React from 'react';
import { X, Chrome, Github, Apple as AppleIcon, Facebook, Instagram, Music2 } from 'lucide-react';

function SignInModal({ show, onClose, onSignIn }) {
  if (!show) return null;

  const providers = [
    {
      id: 'google',
      name: 'Google',
      icon: Chrome,
      color: 'from-red-500 to-yellow-500',
      hoverColor: 'hover:from-red-600 hover:to-yellow-600',
    },
    {
      id: 'facebook',
      name: 'Facebook',
      icon: Facebook,
      color: 'from-blue-600 to-blue-700',
      hoverColor: 'hover:from-blue-700 hover:to-blue-800',
    },
    {
      id: 'apple',
      name: 'Apple',
      icon: AppleIcon,
      color: 'from-gray-800 to-black',
      hoverColor: 'hover:from-gray-900 hover:to-black',
    },
    {
      id: 'azure',
      name: 'Microsoft',
      icon: ({ className }) => (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
          <path d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zm12.6 0H12.6V0H24v11.4z"/>
        </svg>
      ),
      color: 'from-blue-500 to-cyan-500',
      hoverColor: 'hover:from-blue-600 hover:to-cyan-600',
    },
    {
      id: 'instagram',
      name: 'Instagram',
      icon: Instagram,
      color: 'from-purple-600 via-pink-600 to-orange-500',
      hoverColor: 'hover:from-purple-700 hover:via-pink-700 hover:to-orange-600',
    },
    {
      id: 'tiktok',
      name: 'TikTok',
      icon: Music2,
      color: 'from-black via-gray-900 to-teal-500',
      hoverColor: 'hover:from-gray-900 hover:via-black hover:to-teal-600',
    },
  ];

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-3xl shadow-2xl max-w-md w-full relative animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-all duration-200 group z-10"
          aria-label="Close"
        >
          <X className="w-5 h-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
        </button>

        {/* Header */}
        <div className="text-center pt-10 pb-8 px-8">
          {/* Logo/Icon */}
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl mx-auto mb-6 flex items-center justify-center shadow-lg">
            <svg 
              className="w-8 h-8 text-white" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" 
              />
            </svg>
          </div>

          {/* Title */}
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            Welcome Back
          </h2>
          <p className="text-gray-600 text-lg">
            Sign in to continue your journey
          </p>
        </div>

        {/* OAuth Buttons */}
        <div className="px-8 pb-10 space-y-3">
          {providers.map((provider) => {
            const Icon = provider.icon;
            return (
              <button
                key={provider.id}
                onClick={() => onSignIn(provider.id)}
                className={`
                  w-full flex items-center justify-center gap-3 px-6 py-4 
                  bg-gradient-to-r ${provider.color} ${provider.hoverColor}
                  text-white font-semibold rounded-xl 
                  shadow-lg hover:shadow-xl 
                  transform hover:scale-[1.02] active:scale-[0.98]
                  transition-all duration-200
                  group
                `}
              >
                <Icon className="w-5 h-5 group-hover:scale-110 transition-transform" />
                <span>Continue with {provider.name}</span>
              </button>
            );
          })}
        </div>

        {/* Divider */}
        <div className="relative px-8 pb-6">
          <div className="absolute inset-0 flex items-center px-8">
            <div className="w-full border-t border-gray-200"></div>
          </div>
          <div className="relative flex justify-center">
            <span className="px-4 bg-white text-sm text-gray-500">
              Secure authentication
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 pb-8 text-center">
          <p className="text-sm text-gray-500">
            By continuing, you agree to our{' '}
            <a href="/terms" className="text-blue-600 hover:text-blue-700 font-medium">
              Terms of Service
            </a>
            {' '}and{' '}
            <a href="/privacy-policy" className="text-blue-600 hover:text-blue-700 font-medium">
              Privacy Policy
            </a>
          </p>
        </div>

        {/* Bottom Accent */}
        <div className="h-2 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-b-3xl"></div>
      </div>
    </div>
  );
}

export default SignInModal;
